import { RecurringSchedule } from './recurringSchedule';

const MS_PER_DAY = 86_400_000;

// Safe modulo
const mod = (a: number, n: number): number => ((a % n) + n) % n;

// ---- Core logic in day-units only ----
export function suggestAlignedDates({
  startYMD,
  chosenEndYMD,
  period,
  frequency,
}: {
  startYMD: string;
  chosenEndYMD: string;
  period: string;
  frequency: number;
}): {
  alignedYMD: string | null;
  previousYMD: string | null;
  nextYMD: string | null;
  alignedDate: Date | null;
  previousDate: Date | null;
  nextDate: Date | null;
} {
  if (!['day', 'week', 'month', 'year'].includes(period)) {
    throw new Error('Invalid period. Use "day", "week", "month", or "year".');
  }

  let parsedFrequency = frequency;

  try {
    parsedFrequency = parseInt(frequency.toString());
  } catch (error) {
    throw new Error('frequency must be a number');
  }

  if (!Number.isInteger(parsedFrequency) || parsedFrequency <= 0) {
    throw new Error('frequency must be a positive integer');
  }

  const start = parseYMD(startYMD);
  const chosen = parseYMD(chosenEndYMD);

  let previous,
    next,
    aligned: { y?: number; m?: number; d?: number } | null = null;

  if (period === 'day' || period === 'week') {
    const periodDays = period === 'day' ? parsedFrequency : parsedFrequency * 7;
    const startDay = toEpochDay(start);
    const chosenDay = toEpochDay(chosen);
    const r = mod(chosenDay - startDay, periodDays);

    if (r === 0) {
      aligned = chosen;
      previous = chosen;
      next = fromEpochDay(chosenDay + periodDays);
    } else {
      previous = fromEpochDay(chosenDay - r);
      next = fromEpochDay(chosenDay + (periodDays - r));
    }
  } else if (period === 'month') {
    const rough = monthsBetweenRough(start, chosen);
    // nearest multiple of `frequency` not greater than `rough`
    let k = Math.floor(rough / parsedFrequency) * parsedFrequency;

    let candidatePrev = addMonths(start, k);
    // Adjust if we overshot (can happen when chosen is earlier in the month than candidatePrev)
    while (compareDates(candidatePrev, chosen) > 0) {
      k -= parsedFrequency;
      candidatePrev = addMonths(start, k);
    }

    let candidateNext = addMonths(start, k + parsedFrequency);
    while (compareDates(candidateNext, chosen) <= 0) {
      k += parsedFrequency;
      candidatePrev = addMonths(start, k);
      candidateNext = addMonths(start, k + parsedFrequency);
    }

    if (compareDates(candidatePrev, chosen) === 0) {
      aligned = candidatePrev;
      previous = candidatePrev;
      next = candidateNext;
    } else {
      previous = candidatePrev;
      next = candidateNext;
    }
  } else if (period === 'year') {
    const rough = yearsBetweenRough(start, chosen);
    let k = Math.floor(rough / parsedFrequency) * parsedFrequency;

    let candidatePrev = addYears(start, k);
    while (compareDates(candidatePrev, chosen) > 0) {
      k -= parsedFrequency;
      candidatePrev = addYears(start, k);
    }

    let candidateNext = addYears(start, k + parsedFrequency);
    while (compareDates(candidateNext, chosen) <= 0) {
      k += parsedFrequency;
      candidatePrev = addYears(start, k);
      candidateNext = addYears(start, k + parsedFrequency);
    }

    if (compareDates(candidatePrev, chosen) === 0) {
      aligned = candidatePrev;
      previous = candidatePrev;
      next = candidateNext;
    } else {
      previous = candidatePrev;
      next = candidateNext;
    }
  }

  return {
    alignedYMD: aligned ? formatYMD(aligned) : null,
    previousYMD: previous ? formatYMD(previous) : null,
    nextYMD: next ? formatYMD(next) : null,

    // For UI components that want Date objects (safe, local-noon):
    alignedDate: aligned ? toLocalNoonDate(aligned) : null,
    previousDate: previous ? toLocalNoonDate(previous) : null,
    nextDate: next ? toLocalNoonDate(next) : null,
  };
}

function parseYMD(ymd: string): { y: number; m: number; d: number } {
  // "MM/DD/YYYY" -> { y, m, d }
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  return { y, m, d };
}

// Convert a calendar date to an epoch-day integer using UTC (no DST)
function toEpochDay({ y, m, d }: { y: number; m: number; d: number }): number {
  return Math.floor(Date.UTC(y, m - 1, d) / MS_PER_DAY);
}

// Convert epoch-day integer back to {y,m,d}
function fromEpochDay(epochDay: number): { y: number; m: number; d: number } {
  const ms = epochDay * MS_PER_DAY;
  const dt = new Date(ms); // this is a UTC midnight instant
  return {
    y: dt.getUTCFullYear(),
    m: dt.getUTCMonth() + 1,
    d: dt.getUTCDate(),
  };
}

function formatYMD({
  y,
  m,
  d,
}: {
  y?: number;
  m?: number;
  d?: number;
}): string {
  if (!y || !m || !d) return '';
  const MM = String(m).padStart(2, '0');
  const DD = String(d).padStart(2, '0');
  return `${y}-${MM}-${DD}`;
}

// If you need a JS Date for UI widgets, create a *local-noon* Date to avoid DST midnight shifts:
function toLocalNoonDate({
  y,
  m,
  d,
}: {
  y?: number;
  m?: number;
  d?: number;
}): Date {
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

function compareDates(
  a: { y?: number; m?: number; d?: number },
  b: { y?: number; m?: number; d?: number },
) {
  if (!a || !b || !a.y || !b.y || !a.m || !b.m || !a.d || !b.d) return 0;
  if (a.y !== b.y) return a.y - b.y;
  if (a.m !== b.m) return a.m - b.m;
  return a.d - b.d;
}

function daysInMonth(y: number, m: number /* 1-12 */): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function clampDOM(
  y: number,
  m: number,
  d: number,
): { y: number; m: number; d: number } {
  return { y, m, d: Math.min(d, daysInMonth(y, m)) };
}

function addDays(
  date: { y: number; m: number; d: number },
  n: number,
): { y: number; m: number; d: number } {
  const ed = toEpochDay(date) + n;
  return fromEpochDay(ed);
}

function addWeeks(
  date: { y: number; m: number; d: number },
  n: number,
): { y: number; m: number; d: number } {
  return addDays(date, n * 7);
}

function addMonths(
  date: { y: number; m: number; d: number },
  n: number,
): { y: number; m: number; d: number } {
  const total = date.y * 12 + (date.m - 1) + n;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return clampDOM(y, m, date.d);
}

function addYears(
  date: { y: number; m: number; d: number },
  n: number,
): { y: number; m: number; d: number } {
  return clampDOM(date.y + n, date.m, date.d);
}

function monthsBetweenRough(
  a: { y: number; m: number; d: number },
  b: { y: number; m: number; d: number },
): number {
  // Difference ignoring day-of-month; sign matches b >= a or not
  return (b.y - a.y) * 12 + (b.m - a.m);
}
function yearsBetweenRough(
  a: { y: number; m: number; d: number },
  b: { y: number; m: number; d: number },
): number {
  return b.y - a.y;
}
